import { SearchIcon } from "lucide-react";
import { SelectionOption } from "./selection-option";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type DropdownContentProps = {
  /** Filtered options to display in the dropdown */
  filteredOptions: MultiSelectOptionT[];
  /** Currently selected option values */
  selectedValues: string[];
  /** Current search input value */
  searchValue: string;
  /** Callback when search value changes */
  onSearchChange?: (value: string) => void;
  /** Callback when an option is selected */
  onSelect: (value: string) => void;
  /** Placeholder text for the search input */
  searchPlaceholder: string;
  /** Message shown when no options match the search */
  emptyMessage: string;
  /** Maximum number of selections allowed */
  maxSelections?: number;
  /** Whether the component is read-only (prevents selection) */
  readOnly?: boolean;
};

/**
 * Dropdown content for the multi-select popover.
 *
 * Renders a plain search input and a lightweight list of selectable options —
 * intentionally NOT using cmdk's Command, which indexes/registers every item on
 * mount and makes opening feel sluggish. Search is handled externally via
 * useMultiSelectFiltering, so the list just renders the already-filtered options.
 */
export function DropdownContent({
  filteredOptions,
  selectedValues,
  onSearchChange,
  searchValue,
  onSelect,
  searchPlaceholder,
  emptyMessage,
  maxSelections,
  readOnly = false,
}: DropdownContentProps) {
  return (
    <div className="flex flex-col">
      {onSearchChange && (
        <div className="flex items-center gap-2 border-b border-[var(--c-border-soft-2)] px-3">
          <SearchIcon className="size-4 shrink-0 text-[var(--c-faint)]" />
          <input
            type="text"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            disabled={readOnly}
            className="h-10 w-full bg-transparent text-sm text-[var(--c-text)] outline-none placeholder:text-[var(--c-muted)]"
          />
        </div>
      )}

      <div className="max-h-[300px] overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-[var(--c-muted)]">
            {emptyMessage}
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const isDisabled =
              readOnly ||
              option.disabled ||
              (maxSelections &&
                !isSelected &&
                selectedValues.length >= maxSelections);

            return (
              <SelectionOption
                key={option.value}
                option={option}
                isSelected={isSelected}
                isDisabled={!!isDisabled}
                onSelect={onSelect}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
